/**
 * PickingListsBlock — Iter 400.2.
 *
 * Compact picking-lists preview for the customer Work tab. Each row
 * links to the full picking list detail page; techs can drill in and
 * pick/dispatch straight from there. Follows the same visual pattern
 * as JobCardsBlock / TicketsBlock (FoldBlock shell + row grid).
 *
 * Why only a preview here (no create button)? Picking creation lives
 * on the project screen where the source Sales Order lives. From the
 * customer page you're just navigating INTO existing lists.
 */
import { PackageCheck, ChevronRight, Truck, PackageX, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import FoldBlock from "./FoldBlock";

const PHASE_META = {
  open:        { icon: PackageCheck, color: "#22d3ee", label: "Open" },
  dispatched:  { icon: Truck,        color: "#a78bfa", label: "Dispatched" },
  returned:    { icon: PackageX,     color: "#f59e0b", label: "Returned" },
  closed:      { icon: Lock,         color: "#647087", label: "Closed" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function PickingListsBlock({
  lists = [],
  loading = false,
  defaultOpen = false,
}) {
  const count = lists.length;
  return (
    <FoldBlock
      icon={PackageCheck}
      iconColor="#22d3ee"
      title="Picking slips"
      count={count}
      defaultOpen={defaultOpen}
      testId="customer-picking-block"
      emptyHint={
        loading
          ? "Loading picking slips…"
          : "No picking slips yet for this customer. Create one from a project's Sales Order."
      }
    >
      <ul className="divide-y divide-white/5" data-testid="customer-picking-list">
        {lists.map((l) => {
          const phase = PHASE_META[l.derived_phase] || PHASE_META.open;
          const Icon = phase.icon;
          const rows = l.rows_total || 0;
          const picked = l.qty_picked || 0;
          const dispatched = l.qty_dispatched || 0;
          const req = l.qty_required || 0;
          return (
            <li key={l.id} data-testid={`customer-picking-row-${l.id}`}>
              <Link
                to={`/picking/lists/${l.id}`}
                className="flex items-center gap-3 py-2.5 px-1 hover:bg-white/[0.03] transition-colors"
              >
                <div
                  className="w-8 h-8 rounded-md flex items-center justify-center border shrink-0"
                  style={{
                    borderColor: `${phase.color}40`,
                    background: `${phase.color}14`,
                    color: phase.color,
                  }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-white truncate">
                      {l.name || l.short_id || "Picking slip"}
                    </span>
                    <span
                      className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded shrink-0"
                      style={{ background: `${phase.color}22`, color: phase.color }}
                    >
                      {phase.label}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#647087] flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span>Created {fmtDate(l.created_at)}</span>
                    <span className="font-mono">
                      {rows} row{rows === 1 ? "" : "s"}
                    </span>
                    <span className="font-mono">
                      Req <span className="text-[#a0aabf]">{req}</span>
                      {" · "}Picked <span className="text-emerald-300">{picked}</span>
                      {" · "}Disp <span className="text-sky-300">{dispatched}</span>
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#647087] shrink-0" />
              </Link>
            </li>
          );
        })}
      </ul>
    </FoldBlock>
  );
}
